import { useApi } from '@/api';
import { ChartSkeleton, ChartTimeFilter } from '@/components';
import {
  BarChart,
  CurrencyValue,
  Flex,
  LineChart,
  formatNumber,
  formatDatetime,
  DisplaySwitch
} from '@blockanalitica/ui';
import { useMemo, useState, useCallback } from 'react';
import { useTheme } from 'styled-components';
import { tokensMapping } from '@/utils/tokens';
import { useChartPeriod } from '@/hooks';

const displayOptions = [
  { key: 'balance', value: 'Balance' },
  { key: 'volume', value: 'Volume' }
];

export default function HistoricTabs({ walletAddress }) {
  const theme = useTheme();

  const [activeDisplayOption, setActiveDisplayOption] = useState('balance');
  const [chartDaysAgo, setChartDaysAgo] = useState('30');
  const [chartPeriod, updateChartConfigWithPeriod] =
    useChartPeriod(chartDaysAgo);
  const config = updateChartConfigWithPeriod();
  const timeFilter = useMemo(
    () => (
      <ChartTimeFilter activeOption={chartDaysAgo} onChange={setChartDaysAgo} />
    ),
    [chartDaysAgo]
  );

  const [data, error] = useApi({
    url: `/save/dsr/depositors/${walletAddress}/historic/`,
    queryParams: {
      days_ago: chartDaysAgo
    }
  });

  if (error) {
    throw error;
  }

  const valueFormatter = useMemo(() => {
    return function valueFormatter(value: string) {
      switch (activeDisplayOption) {
        case 'volume':
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: tokensMapping('DAI'),
                iconSize: '0'
              }}
            />
          );
        default:
          return (
            <CurrencyValue
              value={value}
              options={{
                currency: tokensMapping('DAI'),
                iconSize: '3'
              }}
            />
          );
      }
    };
  }, [activeDisplayOption]);

  const yScaleValueFormatter = (value) => {
    return formatNumber(Number(value));
  };

  const datasets = useMemo(() => {
    switch (activeDisplayOption) {
      case 'volume':
        return [
          {
            xField: 'date',
            yField: 'deposit',
            label: 'Exit',
            backgroundColor: theme.colors.chartUp
          },
          {
            xField: 'date',
            yField: 'withdraw',
            label: 'Join',
            backgroundColor: theme.colors.chartDown
          }
        ];
      case 'balance':
        return [
          {
            xField: 'date',
            yField: 'balance'
          }
        ];
    }
  }, [activeDisplayOption, theme.colors]);

  const subValueFormatter = useCallback(
    (value: string) => (
      <>
        {chartPeriod.labelPrefix}
        {formatDatetime(value, { format: chartPeriod.dateFormat })}
      </>
    ),
    [chartPeriod]
  );

  if (data) {
    return (
      <Flex flexDirection="column" gap="xs">
        <DisplaySwitch
          data={displayOptions}
          activeOption={activeDisplayOption}
          onChange={setActiveDisplayOption}
        />
        {activeDisplayOption === 'volume' ? (
          <BarChart
            key={activeDisplayOption}
            data={data.volume}
            datasets={datasets}
            valueFormatter={valueFormatter}
            yScaleValueFormatter={yScaleValueFormatter}
            subValueFormatter={subValueFormatter}
            timeFilter={timeFilter}
            config={config}
          />
        ) : (
          <LineChart
            key={activeDisplayOption}
            data={data.balance}
            valueFormatter={valueFormatter}
            datasets={datasets}
            yScaleValueFormatter={yScaleValueFormatter}
            timeFilter={timeFilter}
            config={config}
          />
        )}
      </Flex>
    );
  }

  return <ChartSkeleton />;
}
